/**
 * Subroom groups (PRP-WEB-2 W1.3). A group is a main room and its ORDERED subrooms
 * (F1, PF2 address subrooms by this order; it goes to Xactimate unchanged).
 *
 * Groups live outside room geometry (`projects.metadata.subroom_groups`, DD1), so a
 * phone that drops unknown room fields cannot drop them. These helpers are pure: they
 * take a `Groups` value and return a new one, and never touch walls (grouping never
 * changes walls, founder ruling 2026-11-02).
 *
 * Shape rules (Xactimate subrooms are one level deep):
 * - a room is in at most one group, as its main or as one of its subrooms;
 * - a subroom has no subrooms of its own, and a main is nobody's subroom (no nesting, so no cycles);
 * - a group with no subrooms is dropped;
 * - a subroom is on its main room's level (one room on one floor; refused as 'level' when the
 *   caller passes the plan).
 */
import type { Fail, Plan } from './types';

export interface SubroomGroup { main_room_id: string; subroom_ids: string[] }
export type Groups = SubroomGroup[];

export type GroupResult = { ok: true; groups: Groups } | Fail<'self' | 'grouped' | 'cycle' | 'missing' | 'level'>;

/** The plan-side level of a room, compared case- and space-insensitively (missing = ''). */
const levelKey = (plan: Plan, rid: string): string | null => {
  const r = plan.rooms.find((x) => x.id === rid);
  return r ? (r.level ?? '').trim().toLowerCase().replace(/\s+/g, ' ') : null;
};

const copy = (g: Groups): Groups => g.map((x) => ({ main_room_id: x.main_room_id, subroom_ids: [...x.subroom_ids] }));

/** Where room `rid` sits: its group, its role, and (for a subroom) its 1-based order. Null when ungrouped. */
export function groupOf(
  groups: Groups, rid: string,
): { index: number; group: SubroomGroup; role: 'main' | 'sub'; order: number | null } | null {
  for (let index = 0; index < groups.length; index++) {
    const group = groups[index];
    if (group.main_room_id === rid) return { index, group, role: 'main', order: null };
    const k = group.subroom_ids.indexOf(rid);
    if (k >= 0) return { index, group, role: 'sub', order: k + 1 };
  }
  return null;
}

/**
 * Make `subId` a subroom of `mainId`, at 0-based position `at` (default: last).
 * Refused when the two are the same room ('self'), `subId` is already in a group
 * ('grouped'; remove it first), the result would nest ('cycle': `subId` is a main
 * with subrooms of its own, or `mainId` is itself a subroom), or, given `plan`, the two rooms
 * are on different levels ('level').
 */
export function addToGroup(groups: Groups, mainId: string, subId: string, at?: number, plan?: Plan): GroupResult {
  if (mainId === subId) return { ok: false, err: 'self', message: 'A room cannot be its own subroom' };
  if (plan) {
    const a = levelKey(plan, mainId), b = levelKey(plan, subId);
    if (a !== null && b !== null && a !== b) return { ok: false, err: 'level', message: "Rooms on different levels can't be grouped." };
  }
  const s = groupOf(groups, subId);
  if (s?.role === 'sub') return { ok: false, err: 'grouped', message: 'That room is already a subroom. Remove it from its group first.' };
  if (s?.role === 'main') return { ok: false, err: 'cycle', message: 'That room has subrooms of its own. A subroom cannot have subrooms.' };
  const m = groupOf(groups, mainId);
  if (m?.role === 'sub') return { ok: false, err: 'cycle', message: 'That room is a subroom. A subroom cannot have subrooms.' };
  const out = copy(groups);
  let g = m ? out[m.index] : null;
  if (!g) { g = { main_room_id: mainId, subroom_ids: [] }; out.push(g); }
  const k = at == null ? g.subroom_ids.length : Math.max(0, Math.min(g.subroom_ids.length, Math.trunc(at)));
  g.subroom_ids.splice(k, 0, subId);
  return { ok: true, groups: out };
}

/**
 * Take room `rid` out of its group. A subroom leaves its group (the group goes when
 * it was the last); a main room dissolves its group. Unchanged when it is ungrouped.
 */
export function removeFromGroup(groups: Groups, rid: string): Groups {
  const at = groupOf(groups, rid);
  if (!at) return copy(groups);
  const out = copy(groups);
  if (at.role === 'main') out.splice(at.index, 1);
  else {
    out[at.index].subroom_ids = out[at.index].subroom_ids.filter((x) => x !== rid);
    if (!out[at.index].subroom_ids.length) out.splice(at.index, 1);
  }
  return out;
}

/** Move subroom `subId` of `mainId`'s group to 0-based position `to` (clamped). */
export function reorderGroup(groups: Groups, mainId: string, subId: string, to: number): GroupResult {
  const at = groupOf(groups, subId);
  if (!at || at.role !== 'sub' || at.group.main_room_id !== mainId) {
    return { ok: false, err: 'missing', message: 'That room is not a subroom of this group' };
  }
  const out = copy(groups);
  const ids = out[at.index].subroom_ids.filter((x) => x !== subId);
  ids.splice(Math.max(0, Math.min(ids.length, Math.trunc(to))), 0, subId);
  out[at.index].subroom_ids = ids;
  return { ok: true, groups: out };
}

/**
 * Drop room ids that no longer exist (a room deleted on any device), then repair the
 * shape: the first appearance of a room wins (a later double is dropped), a group whose
 * main is gone or that has no subrooms goes. Order is kept. Takes a plan or room ids.
 */
export function pruneGroups(groups: Groups | null | undefined, rooms: Plan | readonly string[]): Groups {
  const ids = new Set(Array.isArray(rooms) ? rooms : (rooms as Plan).rooms.map((r) => r.id));
  const seen = new Set<string>();
  const out: Groups = [];
  for (const g of groups ?? []) {
    if (!g || typeof g.main_room_id !== 'string' || !ids.has(g.main_room_id) || seen.has(g.main_room_id)) continue;
    const subs: string[] = [];
    for (const s of Array.isArray(g.subroom_ids) ? g.subroom_ids : []) {
      if (typeof s !== 'string' || !ids.has(s) || seen.has(s) || s === g.main_room_id || subs.includes(s)) continue;
      subs.push(s);
    }
    if (!subs.length) continue;
    seen.add(g.main_room_id);
    subs.forEach((s) => seen.add(s));
    out.push({ main_room_id: g.main_room_id, subroom_ids: subs });
  }
  return out;
}
